import { useState } from 'react'
import type { FlowerDay } from '../api/types'
import { Card, Empty, Field, Note, PageHead, Wt } from '../components/ui'
import { useApi } from '../hooks/useApi'
import { today } from '../lib/format'

/**
 * Each flower: how much came in from the growers, and how much went out over
 * the counter.
 *
 * <p>The shop has written both halves every day for years and never been able
 * to see them together - the deliveries are in one book and the sales in
 * another, so "how much موتیا came in today" and "how much موتیا sold" were two
 * separate countings nobody had time for by evening.
 *
 * <p>Deliberately only those two numbers. A subtracted column would read as
 * stock gone missing, and it is not: flowers that arrive in the evening are sold
 * the next morning, so the two sides are not supposed to agree on any one day.
 * Side by side they tell you what you need anyway.
 *
 * <p>And no total row. Every line is a different flower - and two of them,
 * کیش and خرچہ, are not flowers at all - so a column of them added together
 * is a number with no meaning that still looks like an answer.
 */
export function FlowersInOut() {
  const [from, setFrom] = useState(today())
  const [to, setTo] = useState(today())

  const flowers = useApi<FlowerDay[]>(`/reports/flowers?from=${from}&to=${to}`, [from, to])
  const rows = flowers.data ?? []

  return (
    <>
      <PageHead title="آیا اور بکا" hint="کس پھول کا کتنا مال آیا، اور کتنا بکا۔" />

      <Card>
        <div className="row">
          <Field label="اس تاریخ سے">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>

          <Field label="اس تاریخ تک">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Card>

      {flowers.error && <Note kind="error">{flowers.error}</Note>}

      <Card title="پھول کا حساب">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>قسم</th>
                <th>آیا</th>
                <th>بکا</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td className="num">
                    {Number(row.came) > 0 ? <Wt value={row.came} /> : '—'}
                  </td>
                  <td className="num">
                    {Number(row.sold) > 0 ? <Wt value={row.sold} /> : '—'}
                  </td>
                </tr>
              ))}

              {rows.length === 0 && !flowers.loading && (
                <tr>
                  <td colSpan={3}>
                    <Empty>اِن دنوں میں نہ مال آیا نہ بکا۔</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="hint">
          آیا اور بکا ایک دن میں برابر نہیں ہوتے — شام کا آیا ہوا مال اگلی صبح بکتا ہے۔
        </p>
      </Card>
    </>
  )
}
